import { useFetch } from "../../hooks/useFetch";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuthContext } from "../../hooks/useAuthContext";
import { useMemo, useEffect } from "react";
import Loading from "../Loading";
import { calculateTax } from "../../utils/taxCalculations";

import "./UserOrders.css";

const ORDERS_PER_PAGE = 10;

const UserOrders = () => {
  const { user } = useAuthContext();
  const { data, isPending, error } = useFetch(`/api/orders/user/${user.uid}`);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read directly from URL params
  const filterType = searchParams.get("filter") || "all";
  const sortBy = searchParams.get("sort") || "newest";
  const searchQuery = searchParams.get("search") || "";
  const currentPage = parseInt(searchParams.get("page")) || 1;

  const handleDetails = (order) => {
    navigate(`/orders/${order._id}`);
  };

  const updateSearchParams = (updates) => {
    const params = {};

    // Preserve existing params
    if (filterType !== "all") params.filter = filterType;
    if (sortBy !== "newest") params.sort = sortBy;
    if (searchQuery) params.search = searchQuery;
    if (currentPage !== 1) params.page = currentPage;

    // Apply updates
    Object.keys(updates).forEach((key) => {
      if (
        updates[key] === null ||
        updates[key] === "all" ||
        updates[key] === "newest" ||
        updates[key] === "" ||
        updates[key] === 1
      ) {
        delete params[key];
      } else {
        params[key] = updates[key];
      }
    });

    setSearchParams(params);
  };

  const getStatusBadgeClass = (orderState) => {
    const stateMap = {
      placed: "status-placed",
      submitted: "status-submitted",
      fulfilled: "status-fulfilled",
      cancelled: "status-cancelled",
    };
    return stateMap[orderState] || "status-default";
  };

  const getStatusDisplayText = (orderState) => {
    const displayMap = {
      placed: "PLACED",
      pending: "PLACED",
      submitted: "PLACED",
      fulfilled: "PICKED UP",
      cancelled: "CANCELLED",
    };
    return displayMap[orderState?.toLowerCase()] || "PENDING";
  };

  const groupOrdersByDate = (orders) => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    const groups = {
      thisMonth: [],
      lastMonth: [],
      earlier: [],
    };

    orders.forEach((order) => {
      const orderDate = new Date(order.orderTimestamp);
      const orderMonth = orderDate.getMonth();
      const orderYear = orderDate.getFullYear();

      if (orderYear === currentYear && orderMonth === currentMonth) {
        groups.thisMonth.push(order);
      } else if (
        (orderYear === currentYear && orderMonth === currentMonth - 1) ||
        (currentMonth === 0 &&
          orderYear === currentYear - 1 &&
          orderMonth === 11)
      ) {
        groups.lastMonth.push(order);
      } else {
        groups.earlier.push(order);
      }
    });

    return groups;
  };

  const filteredAndSortedOrders = useMemo(() => {
    if (!data) return [];

    // Filter by fulfillment type
    let filtered = data;
    if (filterType !== "all") {
      filtered = data.filter(
        (order) =>
          order.fulfillment?.type?.toLowerCase() === filterType.toLowerCase()
      );
    }

    // Filter by search query (search in order ID, products, and date)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((order) => {
        // Search in order ID
        if (order._id.toLowerCase().includes(query)) return true;

        // Search in product descriptions
        const hasMatchingProduct = order.products.some((product) =>
          product.description?.toLowerCase().includes(query)
        );
        if (hasMatchingProduct) return true;

        // Search in formatted date
        const dateStr = new Date(order.orderTimestamp)
          .toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          })
          .toLowerCase();
        if (dateStr.includes(query)) return true;

        return false;
      });
    }

    // Sort
    let sorted = [...filtered];
    switch (sortBy) {
      case "newest":
        sorted.sort(
          (a, b) => new Date(b.orderTimestamp) - new Date(a.orderTimestamp)
        );
        break;
      case "oldest":
        sorted.sort(
          (a, b) => new Date(a.orderTimestamp) - new Date(b.orderTimestamp)
        );
        break;
      case "highest":
        sorted.sort((a, b) => b.orderTotal - a.orderTotal);
        break;
      case "lowest":
        sorted.sort((a, b) => a.orderTotal - b.orderTotal);
        break;
      default:
        break;
    }

    return sorted;
  }, [data, filterType, sortBy, searchQuery]);

  const groupedOrders = useMemo(() => {
    return groupOrdersByDate(filteredAndSortedOrders);
  }, [filteredAndSortedOrders]);

  // Pagination
  const totalPages = Math.ceil(
    filteredAndSortedOrders.length / ORDERS_PER_PAGE
  );
  const paginatedOrders = useMemo(() => {
    const startIndex = (currentPage - 1) * ORDERS_PER_PAGE;
    const endIndex = startIndex + ORDERS_PER_PAGE;
    return filteredAndSortedOrders.slice(startIndex, endIndex);
  }, [filteredAndSortedOrders, currentPage]);

  const paginatedGroupedOrders = useMemo(() => {
    return groupOrdersByDate(paginatedOrders);
  }, [paginatedOrders]);

  const renderOrderCard = (order) => {
    const firstThreeProducts = order.products.slice(0, 3);
    const remainingCount = order.products.length - 3;
    const subtotal = (order.products || []).reduce(
      (sum, product) =>
        sum + Number(product.casePrice || 0) * Number(product.quantity || 0),
      0
    );
    const tax =
      order?.stripeAmounts?.tax !== undefined &&
      order?.stripeAmounts?.tax !== null
        ? Number(order.stripeAmounts.tax || 0)
        : order.taxRate
        ? calculateTax(subtotal, order.taxRate)
        : 0;
    const deliveryFee =
      order.fulfillment?.type === "Delivery" && order.fulfillment?.deliveryFee
        ? Number(order.fulfillment.deliveryFee)
        : 0;
    const discount =
      order?.stripeAmounts?.discount !== undefined &&
      order?.stripeAmounts?.discount !== null
        ? Number(order.stripeAmounts.discount || 0)
        : Math.max(
            0,
            subtotal + tax + deliveryFee - Number(order.orderTotal || 0)
          );

    return (
      <div
        key={order._id}
        className="orderPreview"
        onClick={() => handleDetails(order)}
      >
        <div className="order-main-content">
          <div className="order-header">
            <div className="order-date-amount">
              <h3>
                {new Date(order.orderTimestamp).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </h3>
              <p className="order-total">
                ${Number.parseFloat(order.orderTotal / 100).toFixed(2)}
              </p>
              {discount > 0 && (
                <p className="order-discount">
                  Saved ${(discount / 100).toFixed(2)} with coupon
                </p>
              )}
            </div>
            <span
              className={`status-badge ${getStatusBadgeClass(
                order.orderState
              )}`}
            >
              {getStatusDisplayText(order.orderState)}
            </span>
          </div>

          <div className="order-info">
            <div className="order-details-row">
              <span className="order-items-count">
                {order.products.length} Items
              </span>
              <span className="order-fulfillment-type">
                {order.fulfillment?.type || "N/A"}
              </span>
            </div>

            {firstThreeProducts.length > 0 && (
              <div className="order-products-preview">
                {firstThreeProducts.map((product, idx) => (
                  <span key={idx} className="product-preview-item">
                    {product.description}
                  </span>
                ))}
                {remainingCount > 0 && (
                  <span className="product-preview-more">
                    +{remainingCount} more
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderOrderGroup = (title, orders) => {
    if (orders.length === 0) return null;

    return (
      <div className="order-group" key={title}>
        <h2 className="order-group-title">{title}</h2>
        {orders.map((order) => renderOrderCard(order))}
      </div>
    );
  };

  return (
    <div className="user-orders-container">
      <div className="orders-header">
        <h1>Previous Orders</h1>

        {data && data.length > 0 && (
          <>
            <div className="search-bar">
              <input
                type="text"
                placeholder="Search orders by product, date, or order ID..."
                value={searchQuery}
                onChange={(e) =>
                  updateSearchParams({ search: e.target.value, page: 1 })
                }
                className="search-input"
              />
              {searchQuery && (
                <button
                  className="clear-search-btn"
                  onClick={() => updateSearchParams({ search: "", page: 1 })}
                  aria-label="Clear search"
                >
                  ✕
                </button>
              )}
            </div>

            <div className="orders-controls">
              <div className="filter-control">
                <label htmlFor="filter">Filter:</label>
                <select
                  id="filter"
                  value={filterType}
                  onChange={(e) =>
                    updateSearchParams({ filter: e.target.value, page: 1 })
                  }
                  className="filter-select"
                >
                  <option value="all">All Orders</option>
                  <option value="delivery">Delivery</option>
                  <option value="pickup">Pickup</option>
                </select>
              </div>

              <div className="sort-control">
                <label htmlFor="sort">Sort:</label>
                <select
                  id="sort"
                  value={sortBy}
                  onChange={(e) =>
                    updateSearchParams({ sort: e.target.value, page: 1 })
                  }
                  className="sort-select"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="highest">Highest Amount</option>
                  <option value="lowest">Lowest Amount</option>
                </select>
              </div>
            </div>
          </>
        )}
      </div>

      {isPending && <Loading />}

      {data && filteredAndSortedOrders.length > 0 && (
        <>
          <div className="orders-list">
            {renderOrderGroup("This Month", paginatedGroupedOrders.thisMonth)}
            {renderOrderGroup("Last Month", paginatedGroupedOrders.lastMonth)}
            {renderOrderGroup("Earlier", paginatedGroupedOrders.earlier)}
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() =>
                  updateSearchParams({ page: Math.max(1, currentPage - 1) })
                }
                disabled={currentPage === 1}
              >
                Previous
              </button>

              <div className="pagination-info">
                <span className="page-numbers">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (page) => (
                      <button
                        key={page}
                        className={`page-number ${
                          page === currentPage ? "active" : ""
                        }`}
                        onClick={() => updateSearchParams({ page })}
                      >
                        {page}
                      </button>
                    )
                  )}
                </span>
                <span className="page-count">
                  Page {currentPage} of {totalPages}
                </span>
              </div>

              <button
                className="pagination-btn"
                onClick={() =>
                  updateSearchParams({
                    page: Math.min(totalPages, currentPage + 1),
                  })
                }
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {data && data.length === 0 && (
        <div className="empty-state">
          <p className="empty-state-message">You have no previous orders</p>
          <button className="button" onClick={() => navigate("/products")}>
            Start Shopping
          </button>
        </div>
      )}

      {data && data.length > 0 && filteredAndSortedOrders.length === 0 && (
        <div className="empty-state">
          <p className="empty-state-message">
            No orders match your search or filters
          </p>
          <button
            className="button-secondary"
            onClick={() => {
              setSearchParams({});
            }}
          >
            Clear Filters
          </button>
        </div>
      )}

      {error && <p className="error-message">{error}</p>}
    </div>
  );
};

export default UserOrders;
